/**
 * Parses clipboard text from Excel / Google Sheets / Numbers: tab-separated cells, rows separated
 * by CRLF, LF or CR, quoted cells (`"…"`, quotes doubled) may contain tabs and line breaks. A
 * single trailing line break is ignored. Only a quote at the start of a cell opens quoting.
 */
export function parseTsv(text: string): string[][] {
  if (text === '') return [];
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let i = 0;
  let atCellStart = true;
  const endRow = (): void => {
    row.push(cell);
    rows.push(row);
    row = [];
    cell = '';
    atCellStart = true;
  };
  while (i < text.length) {
    const ch = text[i]!;
    if (atCellStart && ch === '"') {
      i++;
      while (i < text.length) {
        if (text[i] === '"') {
          if (text[i + 1] === '"') {
            cell += '"';
            i += 2;
            continue;
          }
          i++;
          break;
        }
        cell += text[i];
        i++;
      }
      atCellStart = false;
      continue;
    }
    atCellStart = false;
    if (ch === '\t') {
      row.push(cell);
      cell = '';
      atCellStart = true;
      i++;
    } else if (ch === '\r' || ch === '\n') {
      i += ch === '\r' && text[i + 1] === '\n' ? 2 : 1;
      endRow();
      if (i === text.length) return rows;
    } else {
      cell += ch;
      i++;
    }
  }
  endRow();
  return rows;
}

function tsvCell(cell: string): string {
  return /[\t\r\n"]/.test(cell) ? `"${cell.replaceAll('"', '""')}"` : cell;
}

/**
 * Serialises cells for the clipboard (inverse of {@link parseTsv}). Rows are joined with CRLF; a
 * row that is a single empty cell is written as `""` so it survives the round trip.
 */
export function toTsv(rows: readonly (readonly string[])[]): string {
  return rows
    .map((row) => (row.length === 1 && row[0] === '' ? '""' : row.map(tsvCell).join('\t')))
    .join('\r\n');
}
